/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController } from "context";
import { HomeworkProvider } from "context/homework";

// Auth
import { ApiProvider, RequireAuth } from "./context/api.js";
import SignIn from "pages/authentication/sign-in";
import ResetPassword from "pages/authentication/reset-password";
import SignUp from "pages/authentication/sign-up";
import BetaWarning from "components/BetaWarning";

export default function App() {
  const [controller] = useMaterialUIController();
  const { direction, darkMode } = controller;
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes
      .filter((route) => !route.disabled)
      .map((route) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          return (
            <Route
              exact
              path={route.route}
              element={<RequireAuth>{route.component}</RequireAuth>}
              key={route.key}
            />
          );
        }

        return null;
      });

  const content = (
    <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
      <ApiProvider>
        <HomeworkProvider>
          <CssBaseline />
          <Configurator />
          <Routes>
            {getRoutes(routes)}
            <Route path="/login" element={<SignIn />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/create-profile" element={<SignUp />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
          {/* TODO : Remove the warning once first phase is complete */}
          <BetaWarning />
        </HomeworkProvider>
      </ApiProvider>
    </ThemeProvider>
  );

  return direction === "rtl" ? <CacheProvider value={rtlCache}>{content}</CacheProvider> : content;
}
